import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import {
    Accordion, AccordionDetails, AccordionSummary, Box, FormControl,
    InputLabel, MenuItem, Paper, Select,
    Table, TableBody, TableCell, TableContainer, TableRow, TextField, Typography,
} from "@mui/material"
import { Control, Controller } from "react-hook-form"
import { useTranslation } from "react-i18next"
import InksSection from "./InksSection"
import { IOrder } from "./types"

export default function PrintingSection({ control }: { control: Control<IOrder> }) {
    const { t } = useTranslation()

    return (
        <Accordion defaultExpanded disableGutters variant="outlined" sx={{ mb: 2, "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
                <Typography sx={{ fontWeight: "bold" }}>{t("orders.form.printing")}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pb: 2 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 2, mb: 2 }}>
                    <Controller control={control} name="printer_number"
                        render={({ field }) => <TextField {...field} label={t("orders.form.printerNo")} size="small" type="number" fullWidth />}
                    />
                    <Controller control={control} name="printing_width"
                        render={({ field }) => <TextField {...field} label={t("orders.form.widthCms")} size="small" type="number" fullWidth />}
                    />
                    <Controller control={control} name="printing_height"
                        render={({ field }) => <TextField {...field} label={t("orders.form.height")} size="small" type="number" fullWidth />}
                    />
                    <Controller control={control} name="printing_inks_amount"
                        render={({ field }) => (
                            <FormControl size="small" fullWidth>
                                <InputLabel>{t("orders.form.inksAmount")}</InputLabel>
                                <Select {...field} label={t("orders.form.inksAmount")}>
                                    <MenuItem value={0}>{t("orders.form.ink.noInk")}</MenuItem>
                                    <MenuItem value={1}>{t("orders.form.ink.oneInk")}</MenuItem>
                                    <MenuItem value={2}>{t("orders.form.ink.twoInks")}</MenuItem>
                                    <MenuItem value={3}>{t("orders.form.ink.threeInks")}</MenuItem>
                                    <MenuItem value={4}>{t("orders.form.ink.fourInks")}</MenuItem>
                                </Select>
                            </FormControl>
                        )}
                    />
                    <Controller control={control} name="printing_roller"
                        render={({ field }) => (
                            <FormControl size="small" fullWidth>
                                <InputLabel>{t("orders.form.roller")}</InputLabel>
                                <Select {...field} label={t("orders.form.roller")}>
                                    <MenuItem value={0}>{t("orders.form.noRoller")}</MenuItem>
                                    {[25, 28, 30, 33, 35, 37, 40].map(v => <MenuItem key={v} value={v}>{v} cm.</MenuItem>)}
                                </Select>
                            </FormControl>
                        )}
                    />
                    <Controller control={control} name="printing_mounting"
                        render={({ field }) => (
                            <FormControl size="small" fullWidth>
                                <InputLabel>{t("orders.form.mounting")}</InputLabel>
                                <Select {...field} label={t("orders.form.mounting")}>
                                    <MenuItem value={0}>{t("orders.form.noMounting")}</MenuItem>
                                    <MenuItem value={1}>Single</MenuItem>
                                    <MenuItem value={2}>Double</MenuItem>
                                </Select>
                            </FormControl>
                        )}
                    />
                    <Controller control={control} name="printing_threaded"
                        render={({ field }) => (
                            <FormControl size="small" fullWidth>
                                <InputLabel>{t("orders.form.threaded")}</InputLabel>
                                <Select {...field} label={t("orders.form.threaded")}>
                                    <MenuItem value={0}>{t("orders.form.noThreaded")}</MenuItem>
                                    <MenuItem value={1}>N.</MenuItem>
                                    <MenuItem value={2}>V.</MenuItem>
                                </Select>
                            </FormControl>
                        )}
                    />
                </Box>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                    <Table size="small">
                        <TableBody>
                            <TableRow>
                                <TableCell variant="head">{t("orders.form.rollsNo")}</TableCell>
                                <TableCell><Controller control={control} name="printing_rolls_number" render={({ field: { value } }) => <span>{Number(value).toFixed(5)}</span>} /></TableCell>
                                <TableCell variant="head">{t("orders.form.kgPerRoll")}</TableCell>
                                <TableCell><Controller control={control} name="printing_kg_per_roll" render={({ field: { value } }) => <span>{Number(value).toFixed(1)}</span>} /></TableCell>
                                <TableCell variant="head">{t("orders.form.mtPerRoll")}</TableCell>
                                <TableCell><Controller control={control} name="printing_mt_per_roll" render={({ field: { value } }) => <span>{Number(value).toFixed(1)}</span>} /></TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
                    <InksSection control={control} side="A" title={t("orders.form.sideA")} />
                    <InksSection control={control} side="B" title={t("orders.form.sideB")} />
                </Box>
            </AccordionDetails>
        </Accordion>
    )
}
